import type { ContentItem, ContentStatus, ContentFormat } from "@/api/client";
import { PILLARS, FOCUS_AREAS, PRINCIPLES, labelFor } from "@/lib/pmbok";

// ----------------------------------------------------------------------------
// Vocabulary
// ----------------------------------------------------------------------------

export type GroupBy = "channel" | "pillar" | "month" | "piece";

export const CONTENT_STATUSES: { slug: ContentStatus; label: string }[] = [
  { slug: "idea",      label: "Idea" },
  { slug: "drafting",  label: "Drafting" },
  { slug: "scheduled", label: "Scheduled" },
  { slug: "published", label: "Published" },
  { slug: "evergreen", label: "Evergreen" },
  { slug: "killed",    label: "Killed" },
];

/** Statuses shown by default; the rest sit behind the "archived" toggle. */
export const DEFAULT_STATUSES: ContentStatus[] = ["idea", "drafting", "scheduled", "published"];
export const ARCHIVED_STATUSES: ContentStatus[] = ["evergreen", "killed"];

export const CONTENT_FORMATS: { slug: ContentFormat; label: string }[] = [
  { slug: "post",       label: "Post" },
  { slug: "article",    label: "Article" },
  { slug: "newsletter", label: "Newsletter" },
  { slug: "video",      label: "Video" },
  { slug: "short",      label: "Short" },
  { slug: "carousel",   label: "Carousel" },
  { slug: "thread",     label: "Thread" },
  { slug: "livestream", label: "Livestream" },
];

const STATUS_ORDER: Record<ContentStatus, number> = {
  idea: 0,
  drafting: 1,
  scheduled: 2,
  published: 3,
  evergreen: 4,
  killed: 5,
};

export function statusLabel(s: ContentStatus): string {
  return CONTENT_STATUSES.find((x) => x.slug === s)?.label ?? s;
}

export function formatLabel(f: ContentFormat): string {
  return CONTENT_FORMATS.find((x) => x.slug === f)?.label ?? f;
}

export function pillarLabel(slug: string | null | undefined): string {
  return labelFor(PILLARS, slug);
}
export function focusAreaLabel(slug: string | null | undefined): string {
  return labelFor(FOCUS_AREAS, slug);
}
export function principleLabel(slug: string | null | undefined): string {
  return labelFor(PRINCIPLES, slug);
}

// ----------------------------------------------------------------------------
// Dates
// ----------------------------------------------------------------------------

export const SECONDS_PER_DAY = 86400;

function nowSec(): number {
  return Math.floor(Date.now() / 1000);
}

/** Short display: "May 28". */
export function fmtDate(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

/** Value for an <input type="date"> (local YYYY-MM-DD), round-trips parseDateInput. */
export function fmtDateInput(unixSeconds: number | null): string {
  if (unixSeconds == null) return "";
  const d = new Date(unixSeconds * 1000);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Parse a YYYY-MM-DD date input to unix seconds at local midnight; "" → null. */
export function parseDateInput(value: string): number | null {
  const v = value.trim();
  if (v === "") return null;
  const ms = Date.parse(`${v}T00:00:00`);
  return Number.isNaN(ms) ? null : Math.floor(ms / 1000);
}

function monthKey(unixSeconds: number): string {
  const d = new Date(unixSeconds * 1000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function monthLabel(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });
}

// ----------------------------------------------------------------------------
// Scheduling urgency (remap of the home app's delivery urgency)
// ----------------------------------------------------------------------------

export type UrgencyTone = "ontrack" | "soon" | "late";
export interface Urgency {
  tone: UrgencyTone;
  label: string;
  days: number; // signed; negative = overdue
}

export function publishUrgency(scheduledFor: number, now: number = nowSec()): Urgency {
  const days = Math.ceil((scheduledFor - now) / SECONDS_PER_DAY);
  if (days < 0) return { tone: "late", label: `${Math.abs(days)}d overdue`, days };
  if (days === 0) return { tone: "soon", label: "today", days };
  if (days === 1) return { tone: "soon", label: "tomorrow", days };
  if (days <= 3) return { tone: "soon", label: `in ${days}d`, days };
  return { tone: "ontrack", label: `in ${days}d`, days };
}

// ----------------------------------------------------------------------------
// Grouping
// ----------------------------------------------------------------------------

export const NO_CHANNEL_KEY = "__no_channel__";
export const NO_PILLAR_KEY = "__no_pillar__";
export const NO_MONTH_KEY = "__unscheduled__";
export const NO_PIECE_KEY = "__no_piece__";

export interface ContentGroup {
  key: string;
  label: string;
  color?: string | null;
  items: ContentItem[];
}

function isNoneKey(key: string): boolean {
  return (
    key === NO_CHANNEL_KEY || key === NO_PILLAR_KEY || key === NO_MONTH_KEY || key === NO_PIECE_KEY
  );
}

function compareItems(a: ContentItem, b: ContentItem): number {
  const so = STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
  if (so !== 0) return so;
  const an = a.scheduled_for == null;
  const bn = b.scheduled_for == null;
  if (an !== bn) return an ? 1 : -1; // dated first
  if (a.scheduled_for != null && b.scheduled_for != null && a.scheduled_for !== b.scheduled_for) {
    return a.scheduled_for - b.scheduled_for;
  }
  if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
  return a.title.localeCompare(b.title);
}

function pillarIndex(slug: string): number {
  const i = PILLARS.findIndex((p) => p.slug === slug);
  return i === -1 ? PILLARS.length : i;
}

export function groupContent(items: ContentItem[], groupBy: GroupBy): ContentGroup[] {
  const map = new Map<string, ContentGroup>();

  for (const it of items) {
    let key: string;
    let label: string;
    let color: string | null | undefined;

    if (groupBy === "channel") {
      key = it.channel_slug ?? NO_CHANNEL_KEY;
      label = it.channel_name ?? "No channel";
      color = it.channel_color;
    } else if (groupBy === "pillar") {
      key = it.pillar ?? NO_PILLAR_KEY;
      label = it.pillar ? pillarLabel(it.pillar) : "No pillar";
    } else if (groupBy === "piece") {
      key = it.piece ?? NO_PIECE_KEY;
      label = it.piece ?? "No piece";
    } else {
      if (it.scheduled_for == null) {
        key = NO_MONTH_KEY;
        label = "Unscheduled";
      } else {
        key = monthKey(it.scheduled_for);
        label = monthLabel(it.scheduled_for);
      }
    }

    let g = map.get(key);
    if (!g) {
      g = { key, label, color, items: [] };
      map.set(key, g);
    }
    g.items.push(it);
  }

  const groups = [...map.values()];
  for (const g of groups) g.items.sort(compareItems);

  groups.sort((a, b) => {
    const aNone = isNoneKey(a.key);
    const bNone = isNoneKey(b.key);
    if (aNone !== bNone) return aNone ? 1 : -1; // "none" buckets last
    if (groupBy === "pillar") return pillarIndex(a.key) - pillarIndex(b.key);
    if (groupBy === "month") return a.key.localeCompare(b.key);
    return a.label.localeCompare(b.label);
  });

  return groups;
}

// ----------------------------------------------------------------------------
// Summary stats
// ----------------------------------------------------------------------------

export interface ContentSummary {
  total: number;
  byStatus: Record<ContentStatus, number>;
  upcoming: number; // scheduled, due now or later
  overdue: number; // scheduled, past due
  unscheduled: number; // scheduled, no date set
}

export function buildContentSummary(
  items: ContentItem[],
  now: number = nowSec(),
): ContentSummary {
  const byStatus: Record<ContentStatus, number> = {
    idea: 0,
    drafting: 0,
    scheduled: 0,
    published: 0,
    evergreen: 0,
    killed: 0,
  };
  let upcoming = 0;
  let overdue = 0;
  let unscheduled = 0;

  for (const it of items) {
    byStatus[it.status] = (byStatus[it.status] ?? 0) + 1;
    if (it.status === "scheduled") {
      if (it.scheduled_for == null) unscheduled += 1;
      else if (it.scheduled_for < now) overdue += 1;
      else upcoming += 1;
    }
  }

  return { total: items.length, byStatus, upcoming, overdue, unscheduled };
}

// ----------------------------------------------------------------------------
// "What's next" banner
// ----------------------------------------------------------------------------

export interface NextAction {
  text: string;
  detail: string | null;
  item: ContentItem | null;
}

export function nextAction(items: ContentItem[], now: number = nowSec()): NextAction | null {
  const scheduled = items
    .filter((i) => i.status === "scheduled" && i.scheduled_for != null)
    .sort((a, b) => (a.scheduled_for! - b.scheduled_for!));

  const overdue = scheduled.filter((i) => i.scheduled_for! < now);
  if (overdue.length > 0) {
    const it = overdue[0];
    const u = publishUrgency(it.scheduled_for!, now);
    const more = overdue.length > 1 ? ` · ${overdue.length - 1} more overdue` : "";
    return { text: `Publish "${it.title}"`, detail: `${u.label}${more}`, item: it };
  }

  const upcoming = scheduled.filter((i) => i.scheduled_for! >= now);
  if (upcoming.length > 0) {
    const it = upcoming[0];
    const u = publishUrgency(it.scheduled_for!, now);
    return { text: `Next up: "${it.title}"`, detail: `${u.label} · ${fmtDate(it.scheduled_for!)}`, item: it };
  }

  const undated = items
    .filter((i) => i.status === "scheduled" && i.scheduled_for == null)
    .sort((a, b) => a.sort_order - b.sort_order);
  if (undated.length > 0) {
    const it = undated[0];
    return {
      text: "Ready to go out",
      detail: `${it.title} · ${undated.length} finished, no date set`,
      item: it,
    };
  }

  const drafting = items.filter((i) => i.status === "drafting");
  if (drafting.length > 0) {
    return {
      text: `Finish a draft`,
      detail: `${drafting.length} in drafting`,
      item: drafting[0],
    };
  }

  const ideas = items.filter((i) => i.status === "idea");
  if (ideas.length > 0) {
    return {
      text: `Develop an idea`,
      detail: `${ideas.length} idea${ideas.length === 1 ? "" : "s"} waiting`,
      item: ideas[0],
    };
  }

  return null;
}

// ----------------------------------------------------------------------------
// Copy as text (markdown checklist)
// ----------------------------------------------------------------------------

export function renderContentText(groups: ContentGroup[]): string {
  const lines: string[] = ["# Content plan", ""];
  for (const g of groups) {
    lines.push(`## ${g.label}`);
    for (const it of g.items) {
      const checked = it.status === "published" ? "x" : " ";
      const parts: string[] = [statusLabel(it.status)];
      if (it.scheduled_for != null) parts.push(fmtDate(it.scheduled_for));
      parts.push(formatLabel(it.format));
      if (it.pillar) parts.push(pillarLabel(it.pillar));
      if (it.piece) parts.push(it.piece);
      if (it.provenance === "repost") parts.push("Repost");
      lines.push(`- [${checked}] **${it.title}** — ${parts.join(" · ")}`);
    }
    lines.push("");
  }
  return lines.join("\n").trim() + "\n";
}
